import { HoldingClass } from 'types/HoldingClass';
import normalizeCif from 'utils/normalizeCif';

import { ConstitutionExistingPartner } from 'modules/_shared/components/Modals/Constitution/utils/fetchExistingPartnersForConstitution';

import { groupSharesTransferExcelByOperation } from './groupSharesTransferExcelByOperation';
import { mapRawRowsToSharesTransferExcelRows } from './mapRawRowsToSharesTransferExcelRows';
import { resolveHoldingClassForExcel } from './resolveHoldingClassForExcel';
import { simulateSharesTransferLedger } from './simulateSharesTransferLedger';
import {
  SharesTransferExcelPreview,
  SharesTransferExcelRawRow,
  SharesTransferExcelRow,
  SharesTransferExcelValidationError,
  SharesTransferLedgerState,
} from '../types/SharesTransferExcel';

type BuildPreviewParams = {
  rawRows: SharesTransferExcelRawRow[];
  holdingClasses: HoldingClass[];
  existingPartners: ConstitutionExistingPartner[];
  initialLedger: SharesTransferLedgerState;
  selectedTypesByOperationId?: Record<string, string>;
};

export const buildSharesTransferExcelPreview = ({
  rawRows,
  holdingClasses,
  existingPartners,
  initialLedger,
  selectedTypesByOperationId = {},
}: BuildPreviewParams): SharesTransferExcelPreview => {
  const globalErrors: SharesTransferExcelValidationError[] = [];
  const { rows, errors: rowErrors } =
    mapRawRowsToSharesTransferExcelRows(rawRows);

  globalErrors.push(...rowErrors);

  const holdingClassResult = resolveHoldingClassForExcel(holdingClasses);

  if (holdingClassResult.error) {
    globalErrors.push(holdingClassResult.error);
  }

  const existingCifs = new Set(existingPartners.map((partner) => partner.cif));

  const rowsWithPartnerStatus: SharesTransferExcelRow[] = rows.map((row) => ({
    ...row,
    isExistingPartner: existingCifs.has(normalizeCif(row.cif)),
  }));

  let operations = groupSharesTransferExcelByOperation(rowsWithPartnerStatus);

  operations = operations.map((operation) => {
    const selectedType = selectedTypesByOperationId[operation.operationId];

    if (!selectedType) {
      return operation;
    }

    const filteredErrors = operation.errors.filter(
      (error) => error.code !== 'MIXED_OPERATION_TYPES'
    );

    return {
      ...operation,
      selectedOperationType: selectedType,
      needsTypeSelection: false,
      errors: filteredErrors,
      isValid: filteredErrors.length === 0,
    };
  });

  let ledgerErrors: SharesTransferExcelValidationError[] = [];

  if (holdingClassResult.holdingClassId) {
    const simulation = simulateSharesTransferLedger({
      operations,
      initialLedger,
      holdingClassId: holdingClassResult.holdingClassId,
    });

    operations = simulation.operations;
    ledgerErrors = simulation.errors.filter(
      (error) => error.code === 'RANGE_NOT_AVAILABLE'
    );
  }

  const allErrors = [...globalErrors, ...operations.flatMap((op) => op.errors)];

  const uniqueCifs = new Set(rowsWithPartnerStatus.map((row) => row.cif));
  const newPartners = [...uniqueCifs].filter((cif) => !existingCifs.has(cif));

  return {
    operations,
    totalOperations: operations.length,
    totalMovements: rowsWithPartnerStatus.length,
    totalErrors: allErrors.length,
    totalNewPartners: newPartners.length,
    totalExistingPartners: uniqueCifs.size - newPartners.length,
    errors: allErrors,
    holdingClassId: holdingClassResult.holdingClassId,
    holdingClassName: holdingClassResult.holdingClassName,
    holdingClassError: holdingClassResult.error,
  };
};
